import React from 'react';
import clsx from 'clsx';
import loaderSvg from '../../../../../../assets/aica-loader.svg';
import { isEmpty } from '../../../../../../utils/utils';
import { AuditedRowSpec, AUDITED_UNIT_DIVISOR, AUDITED_UNIT_DEFAULT, CLASSIFICATION_COLUMN_LABEL } from '../../auditedStatementLayout';
import { useAuditedStatementTableLogic } from './useAuditedStatementTableLogic';
import SourceFileTag from '../../../../../../common/SourceFileTag/SourceFileTag';
import { REFERENCE_DOCUMENTS_ROW_LABEL } from '../../../../../../common/SourceFileTag/constants';
import { SourceFile, SourceFilesStatus, SourceStatement } from '../../../../../../common/SourceFileTag/types';
import styles from './AuditedStatementTable.module.scss';

type TableHeader = {
    name: string;
    key: string;
    sourceFiles?: SourceFile[];
};
type TableRow = { classificationName?: string; [key: string]: any };

interface AuditedStatementTableProps {
    /** Figma-ordered row model for this statement (Balance Sheet / P&L / Cash Flow). */
    layout: AuditedRowSpec[];
    /** `sourceFilesStatus` is a WHOLE-TABLE fact (did the source-file lookup succeed at all),
     *  never per-header; only read when `referenceDocumentsRow` is passed. */
    tableData: { headers?: TableHeader[]; rows?: TableRow[]; sourceFilesStatus?: SourceFilesStatus };
    loading: boolean;
    /** Display unit for all figures ('Lakh' | 'Crore') — rescales each value at render time. Defaults to Lakh. */
    unit?: string;
    /** Shown instead of the table when the backend has no data at all for this statement. */
    emptyMessage: string;
    /** RECUR-27656 — reports the pixel widths of the real thead cells ([classification, ...values]) after layout/resize, for the sticky header clone. */
    onHeaderCellWidthsChange?: (widths: number[]) => void;
    /** RECUR-27656 — reports .TableContainer's scrollLeft on every horizontal scroll, so the sticky header clone can track it. */
    onHorizontalScroll?: (scrollLeft: number) => void;
    /** RECUR-27656 (Defect 2) — hides the real thead (opacity, not display, so it stays measurable) while a scroll-synced clone is pinned above it. */
    hideHeader?: boolean;
    /** RECUR-27786 — opt-in row-click for the Classification cell of 'item' rows (leaf
     * classifications only; 'subtotal'/'group'/'sub' rows never carry a raw record to drill
     * into). Audited never passes this — its rows stay non-clickable, unchanged. Provisional
     * passes it to preserve its existing click-to-drill-down/reclassify affordance. */
    onClassificationClick?: (backendRow: TableRow) => void;
    /**
     * Opt-in "Reference Documents" row: one label cell plus one cell per value header, rendered
     * as the FIRST row of `<tbody>`, from `headers[].sourceFiles` + `tableData.sourceFilesStatus`.
     *
     * Lives in `<tbody>`, not `<thead>`: a thead row would need duplicating into
     * AuditedStatementHeaderClone (the sticky pinned-header clone) or the pinned header could
     * silently disagree with the unpinned one. `<tbody>` sidesteps that — it just scrolls away
     * like any other body row once the statement pins.
     *
     * Two whole-table facts live here (never per-header): which statement this is, and the
     * callback that opens a file. Absent for Audited/otherMis/PDF-report — none of them pass it,
     * so no row renders there.
     *
     * Passing this prop is a structural opt-in, not a data guarantee — Provisional always passes
     * it regardless of what a given fetch returns, so the row ALSO requires
     * `tableData.sourceFilesStatus` to be present (see `showReferenceDocumentsRow` below): an
     * untagged response must render identically to Audited's own usage — no row, not a label
     * over blank cells.
     */
    referenceDocumentsRow?: {
        statement: SourceStatement;
        onOpen: (docId: string, fileName: string) => void;
    };
    /** DISPLAY label for the first column's `<th>`. Defaults to CLASSIFICATION_COLUMN_LABEL
     * ('Classification'); Provisional overrides to 'Financial Year'. Display-only —
     * `valueHeaders` below still filters on the fixed 'Classification' backend header-name
     * sentinel regardless of this prop. */
    firstColumnLabel?: string;
}

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

// Rescale a backend-formatted ₹ figure (e.g. "12,22,330", "-5,70,00,000") into the selected unit.
// INR (divisor 1, the default) shows the raw value untouched; Lakhs (÷1e5) / Crore (÷1e7) rescale
// and reformat with Indian grouping + 2 decimals. Non-numeric cells (blank, "—", "NA") pass through.
const rescaleValue = (value: unknown, unit: string): unknown => {
    const divisor = AUDITED_UNIT_DIVISOR[unit] ?? 1;
    if (divisor === 1) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '—') return value;
    const num = Number(trimmed.replace(/,/g, ''));
    if (!Number.isFinite(num)) return value;
    return (num / divisor).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const renderCellValue = (value: unknown) => {
    if (isBlank(value)) return '—';
    return value as React.ReactNode;
};

/**
 * Presentational table for ONE audited-financials statement, built to the RECUR-27010
 * redesign mock. Renders the fixed Figma row model (`layout`), joining backend values by
 * `classificationName`. Rows are non-clickable — no chevrons, no drill-down / edit-
 * classification affordance on this surface — every row renders fully expanded. This REPLACES
 * ClassifiedTable for the audited redesign surface only; ClassifiedTable itself is untouched
 * and still backs Provisional Financials. Supports a pinned sticky-header clone (RECUR-27656):
 * `onHeaderCellWidthsChange` / `onHorizontalScroll` report the real thead's measured widths and
 * scroll position, and `hideHeader` hides the real thead while the clone is pinned above it.
 */
const AuditedStatementTable = ({
    layout,
    tableData,
    loading,
    unit = AUDITED_UNIT_DEFAULT,
    emptyMessage,
    onHeaderCellWidthsChange,
    onHorizontalScroll,
    hideHeader,
    onClassificationClick,
    referenceDocumentsRow,
    firstColumnLabel = CLASSIFICATION_COLUMN_LABEL,
}: AuditedStatementTableProps) => {
    const headers = tableData?.headers ?? [];
    const rows = (tableData?.rows ?? []).filter((row) => !isEmpty(row));
    const valueHeaders = headers.filter((header) => header.name !== 'Classification');
    const hasData = !isEmpty(headers) && valueHeaders.length > 0 && rows.length > 0;

    const rowsBySrc = rows.reduce<Record<string, TableRow>>((acc, row) => {
        if (row.classificationName) acc[row.classificationName] = row;
        return acc;
    }, {});

    // Gated on BOTH the caller opting in AND this table's data actually being tagged — a caller
    // can opt in structurally while a given fetch still comes back untagged, which must render
    // identically to Audited's own usage (no row, not a label over blank cells).
    const showReferenceDocumentsRow = !!referenceDocumentsRow && tableData?.sourceFilesStatus !== undefined;

    const { containerRef, theadRowRef, handleScroll } = useAuditedStatementTableLogic({
        hasData,
        tableVisible: !loading,
        valueHeadersLength: valueHeaders.length,
        unit,
        onHeaderCellWidthsChange,
        onHorizontalScroll,
    });

    // Group and sub rows are plain structural label rows — no affordance, no drill-down — and
    // differ only in the row's own class, so both branches below delegate here.
    const renderStructuralRow = (row: AuditedRowSpec, rowKey: string, rowClassName: string) => (
        <tr key={rowKey} className={rowClassName}>
            <td>{row.label}</td>
            {valueHeaders.map((header) => (
                <td key={header.key} />
            ))}
        </tr>
    );

    if (loading) {
        return (
            <div className={styles.LoadingContainer}>
                <img className={styles.LoaderImg} src={loaderSvg} alt="Loading..." height="30%" width="30%" />
            </div>
        );
    }

    if (!hasData) {
        return <div className={styles.Empty}>{emptyMessage}</div>;
    }

    return (
        <div className={styles.TableContainer} ref={containerRef} onScroll={(e) => handleScroll(e.currentTarget.scrollLeft)}>
            <table className={styles.Table}>
                <thead className={hideHeader ? styles.HeaderHidden : undefined}>
                    <tr ref={theadRowRef}>
                        <th className={styles.ClassificationHeaderCell}>{firstColumnLabel}</th>
                        {valueHeaders.map((header) => (
                            <th key={header.key} className={styles.HeaderCell}>
                                {header.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {showReferenceDocumentsRow && referenceDocumentsRow && (
                        <tr className={styles.ReferenceDocumentsRow}>
                            <td>{REFERENCE_DOCUMENTS_ROW_LABEL}</td>
                            {valueHeaders.map((header) => (
                                <td key={header.key}>
                                    <SourceFileTag
                                        sourceFiles={header.sourceFiles}
                                        status={tableData?.sourceFilesStatus}
                                        statement={referenceDocumentsRow.statement}
                                        columnLabel={header.name}
                                        onOpen={referenceDocumentsRow.onOpen}
                                    />
                                </td>
                            ))}
                        </tr>
                    )}
                    {layout.map((row) => {
                        // RECUR-28287: a row opted into hideWhenAbsent (OCI/TCI) renders nothing
                        // at all when the payload has no matching row — unlike a plain row with a
                        // missing src, which still renders '—' (unchanged, existing behaviour).
                        if (row.hideWhenAbsent && row.src && !rowsBySrc[row.src]) return null;

                        const rowKey = `${row.type}-${row.label}`;

                        if (row.type === 'group') {
                            // Render a real first cell + empty value cells (NOT a single colSpan
                            // cell) so the label uses the same sticky-first-column treatment as
                            // every other row: the light-blue heading stays pinned left while the
                            // (empty) value area scrolls horizontally under the band.
                            return renderStructuralRow(row, rowKey, styles.GroupRow);
                        }

                        if (row.type === 'sub') {
                            return renderStructuralRow(row, rowKey, styles.SubRow);
                        }

                        const backendRow = row.src ? rowsBySrc[row.src] : undefined;
                        const noSource = !row.src;
                        const isClickable = row.type === 'item' && !!onClassificationClick && !!backendRow && !backendRow.isAggregated;

                        return (
                            <tr
                                key={rowKey}
                                className={clsx({
                                    [styles.SubtotalRow]: row.type === 'subtotal',
                                    [styles.GrandTotalRow]: row.type === 'subtotal' && row.grand,
                                    [styles.ItemRow]: row.type === 'item',
                                    [styles.MemoRow]: row.type === 'memo',
                                    [styles.MemoRowFlush]: row.type === 'memo' && row.grand,
                                })}
                            >
                                <td
                                    style={isClickable ? { cursor: 'pointer' } : undefined}
                                    onClick={isClickable ? () => onClassificationClick(backendRow as TableRow) : undefined}
                                >
                                    {row.label}
                                </td>
                                {valueHeaders.map((header) => {
                                    const value = rescaleValue(backendRow ? backendRow[header.key] : undefined, unit);
                                    const isNegative = typeof value === 'string' && value.trim().startsWith('-');
                                    return (
                                        <td
                                            key={header.key}
                                            className={clsx({
                                                [styles.Negative]: isNegative,
                                                [styles.NoSource]: noSource,
                                            })}
                                        >
                                            {renderCellValue(value)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

interface AuditedStatementHeaderCloneProps {
    valueHeaders: TableHeader[];
    /** Measured [classification, ...values] widths from the real thead — see `measure()` in useAuditedStatementTableLogic. */
    widths: number[];
    /** Must mirror the real thead's own `firstColumnLabel` (see AuditedStatementTable's prop of
     * the same name) so the pinned clone stays pixel-identical to the header it's standing in
     * for; defaults to CLASSIFICATION_COLUMN_LABEL. */
    firstColumnLabel?: string;
}

/**
 * RECUR-27656 — pinned-band clone of the real thead row, shown while a statement is stuck to the
 * top of the scroller. Reuses this module's `.Table` classes (thead th styles, sticky first-cell
 * treatment) so it renders pixel-identical to the real header, with each cell's width pinned to
 * what was actually measured (auto table layout can stretch columns on a wide screen). Returns
 * null during the transient frame where widths haven't caught up yet with a Consolidated/
 * Standalone switch (header count changed but widths haven't been re-measured).
 *
 * Clones ONLY the `<thead>` row; the "Reference Documents" row lives in `<tbody>` (see
 * AuditedStatementTable's own `referenceDocumentsRow` docblock for why), so there is nothing of
 * that row to duplicate here.
 */
export const AuditedStatementHeaderClone = ({
    valueHeaders,
    widths,
    firstColumnLabel = CLASSIFICATION_COLUMN_LABEL,
}: AuditedStatementHeaderCloneProps) => {
    if (widths.length !== valueHeaders.length + 1) return null;

    const cellStyle = (width: number) => ({ width, minWidth: width, maxWidth: width });

    return (
        <table className={clsx(styles.Table, styles.CloneTable)}>
            <thead>
                <tr>
                    <th className={styles.ClassificationHeaderCell} style={cellStyle(widths[0])}>
                        {firstColumnLabel}
                    </th>
                    {valueHeaders.map((header, index) => (
                        <th key={header.key} className={styles.HeaderCell} style={cellStyle(widths[index + 1])}>
                            {header.name}
                        </th>
                    ))}
                </tr>
            </thead>
        </table>
    );
};

export default AuditedStatementTable;
